
import React, { useState, useRef, ChangeEvent } from 'react';
import AdminLayout from '@/components/admin/AdminLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Send, Check, Image as ImageIcon, RefreshCw, ArrowLeft, Trash2 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/SupabaseAuthContext';

// Define banner size options
export const BANNER_SIZES = {
  square: { width: 500, height: 500, label: "Square (500×500px)" },
  wide: { width: 1200, height: 628, label: "Wide (1200×628px)" },
  tall: { width: 500, height: 750, label: "Tall (500×750px)" },
  facebook: { width: 1200, height: 628, label: "Facebook (1200×628px)" },
  twitter: { width: 1200, height: 675, label: "Twitter (1200×675px)" },
  instagram: { width: 1080, height: 1080, label: "Instagram (1080×1080px)" },
  custom: { width: 0, height: 0, label: "Custom Size" }
};

interface Message {
  role: 'user' | 'ai';
  content: string;
}

interface BannerImage {
  id: string;
  url: string;
  name: string;
  approved: boolean;
  createdAt: Date;
}

const AIBannerPage = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { signOut } = useAuth();
  const [prompt, setPrompt] = useState<string>('');
  const [messages, setMessages] = useState<Message[]>([
    { role: 'ai', content: 'I can help you create banner images for your marketing campaigns. Tell me what kind of banner you want to create.' }
  ]);
  const [bannerSize, setBannerSize] = useState<keyof typeof BANNER_SIZES>('wide');
  const [customWidth, setCustomWidth] = useState<number>(800);
  const [customHeight, setCustomHeight] = useState<number>(400);
  const [isGenerating, setIsGenerating] = useState<boolean>(false);
  const [uploadedImage, setUploadedImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [generatedBanners, setGeneratedBanners] = useState<BannerImage[]>([]);
  const [currentBanner, setCurrentBanner] = useState<string | null>(null);

  const handlePromptSubmit = () => {
    if (!prompt.trim()) return;
    
    // Add user message
    setMessages(prev => [...prev, { role: 'user', content: prompt }]);
    
    // Simulate AI response for now
    setIsGenerating(true);
    
    // In a real implementation, this would call an AI service
    setTimeout(() => {
      setMessages(prev => [...prev, { 
        role: 'ai', 
        content: 'I\'ve created a banner based on your description. What do you think? Would you like to make any changes?' 
      }]);
      
      // Simulate a generated banner
      setCurrentBanner('https://via.placeholder.com/800x400/007bff/ffffff?text=Generated+Banner');
      setIsGenerating(false);
    }, 2000);
    
    setPrompt('');
  };

  const handleApprove = () => {
    if (currentBanner) {
      const newBanner: BannerImage = {
        id: Date.now().toString(),
        url: currentBanner,
        name: `Banner ${generatedBanners.length + 1}`,
        approved: true,
        createdAt: new Date()
      };
      
      setGeneratedBanners(prev => [...prev, newBanner]);
      setCurrentBanner(null);
      
      toast({
        title: "Banner approved",
        description: "Your banner has been added to the banner list",
      });
      
      // Reset conversation
      setMessages([
        { role: 'ai', content: 'Your banner has been saved! Would you like to create another one?' }
      ]);
    }
  };

  const handleImageUpload = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploadedImage(file);
    
    // Create a preview URL
    const previewUrl = URL.createObjectURL(file);
    setImagePreview(previewUrl);
    
    toast({
      title: "Image uploaded",
      description: `${file.name} will be used in your banner generation.`,
    });
  };

  const handleClearImage = () => {
    setUploadedImage(null);
    if (imagePreview) {
      URL.revokeObjectURL(imagePreview);
      setImagePreview(null);
    }
  };

  const handleRegenerateClick = () => {
    toast({
      title: "Regenerating banner",
      description: "Creating a new variation of your banner...",
    });
    
    setIsGenerating(true);
    
    // Simulate regenerating
    setTimeout(() => {
      setCurrentBanner('https://via.placeholder.com/800x400/28a745/ffffff?text=Regenerated+Banner');
      setIsGenerating(false);
      
      setMessages(prev => [...prev, { 
        role: 'ai', 
        content: 'Here\'s a different version of the banner. Is this more what you had in mind?' 
      }]);
    }, 1500);
  };

  return (
    <AdminLayout 
      isLoading={false} 
      onLogout={signOut}
      userRole="admin"
      activeTab="marketing"
    >
      <div className="container mx-auto p-4">
        <div className="mb-6 flex items-center">
          <Button
            variant="ghost"
            onClick={() => navigate('/admin/marketing')}
            className="mr-4"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Marketing
          </Button>
          <h1 className="text-2xl font-bold">AI Banner Creator</h1>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Banner Size Settings */}
          <Card className="col-span-1">
            <CardHeader>
              <CardTitle>Banner Size</CardTitle>
            </CardHeader>
            <CardContent>
              <RadioGroup value={bannerSize} onValueChange={(value) => setBannerSize(value as keyof typeof BANNER_SIZES)}>
                {Object.entries(BANNER_SIZES).map(([key, size]) => (
                  key !== 'custom' ? (
                    <div key={key} className="flex items-center space-x-2 mb-2">
                      <RadioGroupItem value={key} id={key} />
                      <Label htmlFor={key}>{size.label}</Label>
                    </div>
                  ) : null
                ))}
                <div className="flex items-center space-x-2 mb-2">
                  <RadioGroupItem value="custom" id="custom" />
                  <Label htmlFor="custom">Custom Size</Label>
                </div>
              </RadioGroup>

              {bannerSize === 'custom' && (
                <div className="mt-4 grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="width">Width (px)</Label>
                    <Input 
                      id="width" 
                      type="number" 
                      value={customWidth} 
                      onChange={(e) => setCustomWidth(Number(e.target.value))}
                      min={100}
                      max={2000}
                    />
                  </div>
                  <div>
                    <Label htmlFor="height">Height (px)</Label>
                    <Input 
                      id="height" 
                      type="number" 
                      value={customHeight} 
                      onChange={(e) => setCustomHeight(Number(e.target.value))}
                      min={100}
                      max={2000}
                    />
                  </div>
                </div>
              )}

              {/* Image upload section */}
              <div className="mt-6">
                <h3 className="text-md font-medium mb-2">Upload Reference Image (optional)</h3>
                <input 
                  type="file" 
                  ref={fileInputRef}
                  onChange={handleImageUpload}
                  accept="image/jpeg,image/png,image/gif"
                  className="hidden"
                />
                <div className="flex space-x-2">
                  <Button 
                    variant="outline" 
                    onClick={() => fileInputRef.current?.click()}
                    className="w-full"
                  >
                    <ImageIcon className="mr-2 h-4 w-4" />
                    Select Image
                  </Button>
                  {uploadedImage && (
                    <Button 
                      variant="destructive" 
                      size="icon"
                      onClick={handleClearImage}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
                {imagePreview && (
                  <div className="mt-2">
                    <div className="relative aspect-square w-full max-h-40 overflow-hidden rounded-md border">
                      <img 
                        src={imagePreview} 
                        alt="Uploaded" 
                        className="object-cover w-full h-full"
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-1 truncate">
                      {uploadedImage?.name}
                    </p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Chat and Banner Preview */}
          <Card className="col-span-1 lg:col-span-2">
            <CardHeader>
              <CardTitle>Banner Design Chat</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col h-[600px]">
              {/* Chat history */}
              <ScrollArea className="flex-grow mb-4 border rounded-md p-4">
                <div className="space-y-4">
                  {messages.map((message, index) => (
                    <div 
                      key={index} 
                      className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div 
                        className={`max-w-[80%] p-3 rounded-lg ${
                          message.role === 'user' 
                            ? 'bg-primary text-primary-foreground' 
                            : 'bg-muted'
                        }`}
                      >
                        {message.content}
                      </div>
                    </div>
                  ))}
                  {isGenerating && (
                    <div className="flex justify-start">
                      <div className="max-w-[80%] p-3 rounded-lg bg-muted">
                        <div className="flex items-center space-x-2">
                          <div className="h-2 w-2 bg-gray-400 rounded-full animate-bounce"></div>
                          <div className="h-2 w-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                          <div className="h-2 w-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.4s' }}></div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Current banner preview */}
                {currentBanner && (
                  <div className="mt-4">
                    <div className="border rounded-md overflow-hidden">
                      <img 
                        src={currentBanner} 
                        alt="Generated Banner" 
                        className="w-full h-auto"
                      />
                    </div>
                    <div className="mt-2 flex space-x-2">
                      <Button 
                        onClick={handleApprove} 
                        className="flex-1"
                        variant="default"
                      >
                        <Check className="mr-2 h-4 w-4" />
                        Approve Banner
                      </Button>
                      <Button 
                        onClick={handleRegenerateClick} 
                        variant="outline"
                      >
                        <RefreshCw className="mr-2 h-4 w-4" />
                        Regenerate
                      </Button>
                    </div>
                  </div>
                )}
              </ScrollArea>

              {/* Input area */}
              <div className="flex space-x-2 items-end">
                <div className="flex-grow">
                  <Textarea 
                    placeholder="Describe the banner you want to create..."
                    value={prompt}
                    onChange={(e) => setPrompt(e.target.value)}
                    className="min-h-[80px]"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handlePromptSubmit();
                      }
                    }}
                  />
                </div>
                <Button 
                  onClick={handlePromptSubmit} 
                  disabled={isGenerating || !prompt.trim()}
                  size="icon"
                  className="h-10 w-10"
                >
                  <Send className="h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Generated banners list */}
        {generatedBanners.length > 0 && (
          <Card className="mt-6">
            <CardHeader>
              <CardTitle>Your Approved Banners</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {generatedBanners.map((banner) => (
                  <div key={banner.id} className="border rounded-md overflow-hidden">
                    <img 
                      src={banner.url} 
                      alt={banner.name} 
                      className="w-full h-auto"
                    />
                    <div className="p-3 flex justify-between items-center">
                      <span className="font-medium">{banner.name}</span>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => {
                          setCurrentBanner(banner.url);
                          setMessages([
                            { role: 'ai', content: 'I\'ve loaded your previously approved banner for editing. What would you like to change?' }
                          ]);
                          
                          // Scroll to top
                          window.scrollTo({ top: 0, behavior: 'smooth' });
                        }}
                      >
                        Edit
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </AdminLayout>
  );
};

export default AIBannerPage;
